// store/claimsSlice.js
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { claimService } from '../services/claimService';
import { documentService } from '../services/documentService';
import { fetchPatientById } from './patientSlice';
import { fetchProviderById } from './providerSlice';
import { uploadDocument, deleteDocument, updateDocument, searchDocuments } from './documentsSlice';
import { fetchLawyerById } from './lawyerSlice';
import { fetchInsuranceById } from './insuranceSlice';

// Helper to normalize appeal response payloads (fixes no-undef errors)
const normalizeAppealResponse = (payload) => {
  if (!payload) return null;
  const p = payload.data || payload;
  if (Array.isArray(p)) return p.length > 0 ? p[0] : null;
  return p;
};

// Async thunks
export const fetchClaims = createAsyncThunk(
  'claims/fetchClaims',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const modifiedFilters = { ...filters };
      const shouldGetAll = modifiedFilters.getAll;

      if (shouldGetAll) {
        delete modifiedFilters.getAll;
      }

      // If getAll is true, fetch all claims by handling pagination
      if (shouldGetAll) {
        let allClaims = [];
        let page = 1;
        let hasMore = true;

        while (hasMore) {
          const pageFilters = { ...modifiedFilters, page };
          // Use a high limit per page to minimize requests
          pageFilters.limit = 1000;

          const response = await claimService.searchClaims(pageFilters);
          const responseData = response.data || response;
          const pageData = responseData.data || responseData;
          const pagination = responseData.pagination || {};

          if (Array.isArray(pageData) && pageData.length > 0) {
            allClaims = [...allClaims, ...pageData];
          }

          // Check if there are more pages
          hasMore = pagination.hasNextPage || false;
          page++;

          // Safety break to avoid infinite loops
          if (page > 100) {
            console.warn('Reached maximum page limit (100) when fetching all claims');
            break;
          }
        }

        return { data: allClaims, pagination: { totalItems: allClaims.length } };
      }

      // Normal fetch with single page
      const response = await claimService.searchClaims(modifiedFilters);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch claims');
    }
  }
);

export const fetchAppealClaims = createAsyncThunk(
  'claims/fetchAppealClaims',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await claimService.searchClaims(filters);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch appeal claims');
    }
  }
);

export const fetchClaimsByInsurance = createAsyncThunk(
  'claims/fetchClaimsByInsurance',
  async ({ insuranceId, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await claimService.getClaimsByInsurance(insuranceId, page, limit);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch claims for insurance');
    }
  }
);

export const updateClaim = createAsyncThunk(
  'claims/updateClaim',
  async ({ claimId, updates }, { rejectWithValue }) => {
    try {
      const response = await claimService.updateClaim(claimId, updates);
      return response.claim || response.data || response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update claim');
    }
  }
);

export const createClaim = createAsyncThunk(
  'claims/createClaim',
  async (claimData, { rejectWithValue }) => {
    try {
      const response = await claimService.createClaim(claimData);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create claim');
    }
  }
);

export const updateClaimMedications = createAsyncThunk(
  'claims/updateClaimMedications',
  async ({ claimId, medicationUpdates, paymentDetails, status }, { rejectWithValue }) => {
    try {
      const response = await claimService.updateClaimMedications(claimId, medicationUpdates, paymentDetails, status);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update billing');
    }
  }
);

export const fetchClaimWithRelatedData = createAsyncThunk(
  'claims/fetchClaimWithRelatedData',
  async (claimId, { dispatch, rejectWithValue }) => {
    try {
      // 1. Fetch the core claim data first
      const claimDetails = await claimService.getClaim(claimId);
      const claimObj = claimDetails.claim || claimDetails.data || claimDetails;

      // Render the case shell as soon as the core claim is available. The route
      // keeps its blocking spinner only while selectedClaim is empty, so related
      // documents and participants can hydrate progressively below.
      dispatch(selectClaim(claimObj));

      // 2. Create an array of promises to fetch all related data in parallel
      const relatedDataPromises = [];

      // Patient
      if (claimObj.patientId) {
        relatedDataPromises.push(
          dispatch(fetchPatientById(claimObj.patientId))
            .unwrap()
            .then((data) => ({ type: 'patient', data }))
            .catch((e) => ({ type: 'patient', error: e }))
        );
      }

      // Documents
      relatedDataPromises.push(
        dispatch(searchDocuments({ claimId }))
          .unwrap()
          .then((data) => ({ type: 'documents', data }))
          .catch((e) => ({ type: 'documents', error: e }))
      );

      // Provider
      if (claimObj.providerId) {
        relatedDataPromises.push(
          dispatch(fetchProviderById({ id: claimObj.providerId, forceRefresh: true }))
            .unwrap()
            .then((data) => ({ type: 'provider', data }))
            .catch((e) => ({ type: 'provider', error: e }))
        );
      }

      // Lawyer
      if (claimObj.lawyerId) {
        relatedDataPromises.push(
          dispatch(fetchLawyerById({ id: claimObj.lawyerId, forceRefresh: true }))
            .unwrap()
            .then((data) => ({ type: 'lawyer', data }))
            .catch((e) => ({ type: 'lawyer', error: e }))
        );
      }

      // Insurance belongs in the same parallel batch; it previously added a
      // second network round trip after every other related request completed.
      const insuranceId = claimObj.primaryInsuranceId;
      if (insuranceId) {
        relatedDataPromises.push(
          dispatch(fetchInsuranceById({ id: insuranceId, forceRefresh: true }))
            .unwrap()
            .then((data) => ({ type: 'insurance', data }))
            .catch((e) => ({ type: 'insurance', error: e }))
        );
      }

      // 3. Wait for the parallel hydration and preserve the existing payload shape.
      const settledResults = await Promise.allSettled(relatedDataPromises);
      const relatedData = {};
      for (const result of settledResults) {
        if (result.status === 'fulfilled') {
          const { type, data, error } = result.value;
          if (!error) relatedData[type] = data;
        }
      }

      // 4. Return final result
      return {
        ...claimObj,
        ...relatedData, // includes patient, provider, lawyer, documents
      };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch claim details');
    }
  }
);


/**
 * Dev utility thunk - updates claim status (staging only)
 */
export const devUpdateClaimStatus = createAsyncThunk(
  'claims/devUpdateClaimStatus',
  async ({ claimId, status }, { rejectWithValue }) => {
    try {
      if (process.env.REACT_APP_FIREBASE_ENV !== 'staging') {
        throw new Error('Dev utilities only available in staging');
      }

      const response = await claimService.updateClaim(claimId, { status });
      return response.claim || response.data || response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update claim status');
    }
  }
);

// Add new thunks for appeals
export const createAppeal = createAsyncThunk(
  'claims/createAppeal',
  async ({ claimId, appealData }, { rejectWithValue }) => {
    try {
      const response = await claimService.createAppeal(claimId, appealData);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAppeals = createAsyncThunk(
  'claims/fetchAppeals',
  async (claimIdOrFilters, { rejectWithValue }) => {
    try {
      // Backward-compatible:
      // - string => fetch appeals for one claim
      // - object => fetch appealed claims list and flatten into appeal rows for Claims Management
      if (typeof claimIdOrFilters === 'string') {
        const response = await claimService.getAppeals(claimIdOrFilters);
        return { claimId: claimIdOrFilters, appeals: response.data || response };
      }

      const filters = (claimIdOrFilters && typeof claimIdOrFilters === 'object') ? claimIdOrFilters : {};
      const response = await claimService.searchClaims({ hasAppeals: true, ...filters });
      const payload = response.data || response;
      const claims = Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : []);

      const appeals = claims.flatMap((claim) => {
        const patientName = [claim?.firstName, claim?.lastName].filter(Boolean).join(' ').trim()
          || claim?.patientName
          || 'Unknown Patient';
        const claimSummary = {
          patientName,
          caseNumber: claim?.caseNumber,
          dateOfService: claim?.dateOfService,
        };

        const medicationLevel = (claim?.medicationBreakdown || []).flatMap((medication) =>
          (medication?.appealHistory || []).map((appeal) => ({
            ...appeal,
            claimId: claim.id,
            medicationId: appeal?.medicationId || medication?.medicationId,
            claim: claimSummary,
          }))
        );

        const topLevel = (claim?.appeals || []).map((appeal) => ({
          ...appeal,
          claimId: claim.id,
          claim: claimSummary,
        }));

        return [...medicationLevel, ...topLevel];
      });

      return { claimId: null, appeals, mode: 'list' };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const getAppealById = createAsyncThunk(
  'claims/getAppealById',
  async ({ claimId, appealId }, { rejectWithValue }) => {
    try {
      const response = await claimService.getAppealById(claimId, appealId);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const updateAppeal = createAsyncThunk(
  'claims/updateAppeal',
  async ({ claimId, appealId, updates }, { rejectWithValue }) => {
    try {
      const response = await claimService.updateAppeal(claimId, appealId, updates);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const submitAppeal = createAsyncThunk(
  'claims/submitAppeal',
  async ({ claimId, appealId }, { rejectWithValue }) => {
    try {
      const response = await claimService.submitAppeal(claimId, appealId);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

// Update the resolveAppeal thunk to handle the array response properly
export const resolveAppeal = createAsyncThunk(
  'claims/resolveAppeal',
  async ({ claimId, appealId, resolution, paymentInfo, medicationIds, options } = {}, { rejectWithValue }) => {
    try {
      if (!claimId || !appealId) throw new Error('Missing claimId or appealId');
      const response = await claimService.resolveAppeal(claimId, appealId, resolution, paymentInfo, medicationIds, options);
      const resolved = response.data || response;
      return { appealId, resolvedAppeal: resolved };
    } catch (err) {
      return rejectWithValue(err.message || err);
    }
  }
);

export const updateClaimFinancials = createAsyncThunk(
  'claims/updateClaimFinancials',
  async ({ claimId, financialData }, { rejectWithValue }) => {
    try {
      const response = await claimService.updateClaim(claimId, { financialData });
      return response.claim || response.data || response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to update claim financials');
    }
  }
);

export const fetchEOBHistory = createAsyncThunk(
  'claims/fetchEOBHistory',
  async (claimId, { rejectWithValue }) => {
    try {
      const response = await claimService.getEOBHistory(claimId);
      return { claimId, eobHistory: response.data || response };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


export const createBulkAppeals = createAsyncThunk(
  'claims/createBulkAppeals',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await claimService.createBulkAppeals(payload);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to create bulk appeals');
    }
  }
);

export const submitBulkAppeals = createAsyncThunk(
  'claims/submitBulkAppeals',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await claimService.submitBulkAppeals(payload);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to submit bulk appeals');
    }
  }
);

export const resolveBulkAppeals = createAsyncThunk(
  'claims/resolveBulkAppeals',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await claimService.resolveBulkAppeals(payload);
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to resolve bulk appeals');
    }
  }
);

// Add new thunk to fetch forms for a claim
export const fetchClaimForms = createAsyncThunk(
  'claims/fetchClaimForms',
  async (arg, { rejectWithValue }) => {
    try {
      const claimId = typeof arg === 'string' ? arg : arg?.claimId;
      const requestedProviderId = typeof arg === 'string' ? null : (arg?.providerId || null);
      const response = await documentService.searchDocuments({
        claimId,
        documentType: 'CLAIM_FORMS'
      });
      const forms = response.data || response;

      const toMillis = (value) => {
        if (!value) return 0;
        if (value.seconds !== undefined) return value.seconds * 1000;
        if (value._seconds !== undefined) return value._seconds * 1000;
        const parsed = new Date(value).getTime();
        return Number.isNaN(parsed) ? 0 : parsed;
      };

      const pickLatestForm = (docs, subType) => docs
        .filter((doc) => doc?.subType === subType)
        .sort((a, b) => {
          const versionDiff = (b?.version || 0) - (a?.version || 0);
          if (versionDiff !== 0) return versionDiff;
          return toMillis(b?.updatedAt || b?.createdAt) - toMillis(a?.updatedAt || a?.createdAt);
        })[0] || null;

      const byProvider = forms.reduce((acc, doc) => {
        const providerKey = String(doc?.providerId || doc?.providerIds?.[0] || 'no-provider');
        if (!acc[providerKey]) {
          acc[providerKey] = { cms1500: null, rxTransaction: null, paLibc9: null, njForm: null };
        }
        return acc;
      }, {});

      Object.keys(byProvider).forEach((providerKey) => {
        const providerForms = forms.filter((doc) => String(doc?.providerId || doc?.providerIds?.[0] || 'no-provider') === providerKey);
        byProvider[providerKey] = {
          cms1500: pickLatestForm(providerForms, 'CMS1500'),
          rxTransaction: pickLatestForm(providerForms, 'RX_AUDIT'),
          paLibc9: pickLatestForm(providerForms, 'PA-LIBC-9'),
          njForm: pickLatestForm(providerForms, 'NJFORM'),
        };
      });

      const selectedProviderForms = requestedProviderId ? (byProvider[String(requestedProviderId)] || null) : null;

      // Normalize to a reliable structure
      return {
        claimId,
        providerId: requestedProviderId,
        byProvider,
        cms1500: selectedProviderForms?.cms1500 || pickLatestForm(forms, 'CMS1500'),
        rxTransaction: selectedProviderForms?.rxTransaction || pickLatestForm(forms, 'RX_AUDIT'),
        paLibc9: selectedProviderForms?.paLibc9 || pickLatestForm(forms, 'PA-LIBC-9'),
        njForm: selectedProviderForms?.njForm || pickLatestForm(forms, 'NJFORM'),
      };
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);


export const fetchClaimStatusCounts = createAsyncThunk(
  'claims/fetchClaimStatusCounts',
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await claimService.getClaimStatusCounts(filters);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch status counts');
    }
  }
);

export const fetchPatientClaimsHistory = createAsyncThunk(
  'claims/fetchPatientClaimsHistory',
  async (patientId, { rejectWithValue }) => {
    try {
      // Re-use searchClaims but specifically for this patient
      const response = await claimService.searchClaims({
        patientId,
        limit: 50, // Fetch a reasonable number of history items
        sortBy: 'dateOfService',
        sortOrder: 'desc'
      });
      return response.data || response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch patient claim history');
    }
  }
);

export const splitAndProcessEob = createAsyncThunk(
  'claims/splitAndProcessEob',
  async ({ orgId, file }, { rejectWithValue }) => {
    try {
      const response = await claimService.splitAndProcessEob(orgId, file);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to split and process EOB');
    }
  }
);

export const splitClaim = createAsyncThunk(
  'claims/splitClaim',
  async (claimId, { rejectWithValue }) => {
    try {
      const response = await claimService.splitClaim(claimId);
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to split claim');
    }
  }
);

export const manualSplitClaim = createAsyncThunk(
  'claims/manualSplitClaim',
  async ({ claimId, selectedMedicationIds, targetProviderId, targetProviderContactId }, { rejectWithValue }) => {
    try {
      const response = await claimService.manualSplitClaim(claimId, {
        selectedMedicationIds,
        targetProviderId,
        targetProviderContactId
      });
      return response;
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to manually split claim');
    }
  }
);

// Slice
const initialState = {
  claims: [],
  selectedClaim: null,
  totalPages: 1,
  totalClaims: 0,
  totalRxCount: 0,
  loading: false,
  error: null,
  appealClaims: [],
  totalAppealClaims: 0,
  appealClaimsLoading: false,
  statusCounts: {}, // New state for status counts
  statusCountsLoading: false,
  // Past claims history for import
  patientHistoryClaims: [],
  patientHistoryClaimsLoading: false,
  // Cursor-based pagination info
  pagination: {
    nextCursor: null,
    prevCursor: null,
    hasNextPage: false,
    hasPrevPage: false,
  },
  appeals: [],
  eobHistory: [],
  appealLoading: false,
  appealError: null,
  filters: {
    page: 1,
    limit: 10,
    queryText: undefined,
    status: undefined,
    caseType: undefined,
    firstName: undefined,
    lastName: undefined,
    patientId: undefined,
    selectedPatientData: null,
    dateOfServiceStart: undefined,
    dateOfServiceEnd: undefined,
    updatedAtStart: undefined,
    updatedAtEnd: undefined,
    hasAppeals: undefined,
    hasPrescription: undefined,
    isLitigated: undefined,
    fullRxNumber: undefined,
    networkType: undefined,
    ratingRange: undefined,
    providerName: undefined,
    providerOfficeName: undefined,
    name: undefined,
    contact: undefined,
    denialReason: undefined,
    createdBy: undefined,
  },
  reconFilters: {
    page: 1,
    limit: 10,
    queryText: undefined,
    status: undefined,
    caseType: undefined,
    firstName: undefined,
    lastName: undefined,
    patientId: undefined,
    selectedPatientData: null,
    dateOfServiceStart: undefined,
    dateOfServiceEnd: undefined,
    updatedAtStart: undefined,
    updatedAtEnd: undefined,
    hasAppeals: undefined,
    hasPrescription: undefined,
    isTransfered: undefined,
    isLitigated: undefined,
    fullRxNumber: undefined,
    networkType: undefined,
    ratingRange: undefined,
    providerName: undefined,
    providerOfficeName: undefined,
    name: undefined,
    contact: undefined,
    denialReason: undefined,
    createdBy: undefined,
  },
  appealFilters: {
    page: 1,
    limit: 50, // Higher limit usually better for dashboard views
    status: null,
    hasAppeals: true, // Backend flag to only return claims with appeals
  },
  claimForms: {
    // [claimId]: {
    //   cms1500: null,
    //   rxTransaction: null,
    //   paLibc9: null,
    //   loading: false,
    //   error: null,
    // }
  },
  recentlyUpdatedClaimIds: [], // Track updated claims for post-submission filtering
};

const createEmptyClaimFormsState = () => ({
  cms1500: null,
  rxTransaction: null,
  paLibc9: null,
  njForm: null,
  loading: false,
  error: null,
  byProvider: {},
});

const claimsSlice = createSlice({
  name: 'claims',
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
        page: action.payload.page || 1,
      };
    },
    resetFilters: (state) => {
      state.filters = {
        page: 1,
        limit: 10,
        queryText: undefined,
        status: undefined,
        caseType: undefined,
        firstName: undefined,
        lastName: undefined,
        dateOfServiceStart: undefined,
        dateOfServiceEnd: undefined,
        updatedAtStart: undefined,
        updatedAtEnd: undefined,
        hasAppeals: undefined,
        hasPrescription: undefined,
        isTransfered: undefined,
        isLitigated: undefined,
        fullRxNumber: undefined,
        networkType: undefined,
        ratingRange: undefined,
        providerName: undefined,
        providerOfficeName: undefined,
        name: undefined,
        contact: undefined,
        denialReason: undefined,
      };
    },
    setReconFilters: (state, action) => {
      state.reconFilters = {
        ...state.reconFilters,
        ...action.payload,
        page: action.payload.page || 1,
      };
    },
    resetReconFilters: (state) => {
      state.reconFilters = {
        page: 1,
        limit: 10,
        queryText: undefined,
        status: undefined,
        caseType: undefined,
        firstName: undefined,
        lastName: undefined,
        dateOfServiceStart: undefined,
        dateOfServiceEnd: undefined,
        updatedAtStart: undefined,
        updatedAtEnd: undefined,
        hasAppeals: undefined,
        hasPrescription: undefined,
        isTransfered: undefined,
        isLitigated: undefined,
        fullRxNumber: undefined,
        networkType: undefined,
        ratingRange: undefined,
        providerName: undefined,
        providerOfficeName: undefined,
        name: undefined,
        contact: undefined,
        denialReason: undefined,
      };
    },
    setAppealFilters: (state, action) => {
      state.appealFilters = { ...state.appealFilters, ...action.payload };
    },
    setRecentlyUpdatedClaimIds: (state, action) => {
      state.recentlyUpdatedClaimIds = action.payload || [];
    },
    clearRecentlyUpdatedClaimIds: (state) => {
      state.recentlyUpdatedClaimIds = [];
    },
    clearFilters: (state) => {
      state.filters = {
        page: 1,
        limit: 10,
        patientId: null,
        firstName: null,
        lastName: null,
        caseNumber: null,
        status: null,
      };
    },
    setPage: (state, action) => {
      state.filters.page = action.payload;
    },
    setLimit: (state, action) => {
      state.filters.limit = action.payload;
    },
    clearError: (state) => {
      state.error = null;
    },
    selectClaim: (state, action) => {
      state.selectedClaim = action.payload;
    },
    clearSelectedClaim: (state) => {
      state.selectedClaim = null;
    },
    updateCaseTypeOptimistically: (state, action) => {
      const { claimId, caseType } = action.payload;
      const index = state.claims.findIndex(c => c.id === claimId);
      if (index !== -1) state.claims[index].caseType = caseType;
    },
    clearAppealError: (state) => {
      state.appealError = null;
    },
    setClaims: (state, action) => {
      state.claims = action.payload;
      state.totalClaims = action.payload.length;
    },
    setAppealClaims: (state, action) => {
      state.appealClaims = action.payload;
      state.totalAppealClaims = action.payload.length;
    },
  },
  extraReducers: (builder) => {
    builder
      // fetchClaimsByInsurance
      .addCase(fetchClaimsByInsurance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClaimsByInsurance.fulfilled, (state, action) => {
        state.loading = false;
        const payload = action.payload || {};
        const data = payload.data || [];
        const pagination = payload.pagination || {};

        state.claims = data;
        state.totalPages = pagination.totalPages || 1;
        state.totalClaims = pagination.totalItems || data.length;

      })
      .addCase(fetchClaimsByInsurance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
      })
      // fetchClaims
      .addCase(fetchClaims.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClaims.fulfilled, (state, action) => {
        state.loading = false;

        // Handle new unified response structure
        const payload = action.payload || {};
        const data = payload.data || [];
        const pagination = payload.pagination || {};
        const metadata = payload.metadata || {};

        // Data is already sorted by the backend (sortBy/sortOrder params)
        // No frontend sorting needed - improves performance

        state.claims = data;
        state.totalPages = pagination.totalPages || 1;
        state.totalClaims = pagination.totalItems || data.length;
        state.totalRxCount = metadata.totalRxCount || 0;

        // Store cursor info for efficient next/prev navigation
        state.pagination = {
          ...pagination,
          nextCursor: pagination.nextCursor,
          prevCursor: pagination.prevCursor,
          hasNextPage: pagination.hasNextPage,
          hasPrevPage: pagination.hasPrevPage,
        };

        // Store search metadata
        state.searchMetadata = {
          searchType: metadata.searchType || 'filter',
          filtersApplied: metadata.filtersApplied || [],
          executionTimeMs: metadata.executionTimeMs || 0,
          vectorSearchUsed: metadata.vectorSearchUsed || false,
          denialReasons: metadata.denialReasons || [] // New: Store available denial reasons
        };

        // Align current page with response
        if (pagination.page) {
          state.filters.page = pagination.page;
        }
      })
      .addCase(fetchClaims.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
      })

      // fetchAppealClaims
      .addCase(fetchAppealClaims.pending, (state) => {
        state.appealClaimsLoading = true;
        state.error = null;
      })
      .addCase(fetchAppealClaims.fulfilled, (state, action) => {
        state.appealClaimsLoading = false;
        const payload = action.payload || {};
        const data = payload.data || [];
        const pagination = payload.pagination || {};

        state.appealClaims = data;
        state.totalAppealClaims = pagination.totalItems || data.length;
      })
      .addCase(fetchAppealClaims.rejected, (state, action) => {
        state.appealClaimsLoading = false;
        state.error = action.payload || action.error?.message;
      })

      // updateClaim
      .addCase(updateClaim.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateClaim.fulfilled, (state, action) => {
        state.loading = false;
        const updatedClaim = action.payload;
        const index = state.claims.findIndex(c => c.id === updatedClaim.id);
        if (index !== -1) state.claims[index] = updatedClaim;
        if (state.selectedClaim?.id === updatedClaim.id) state.selectedClaim = updatedClaim;
      })
      .addCase(updateClaim.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
      })

      // splitClaim
      .addCase(splitClaim.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(splitClaim.fulfilled, (state, action) => {
        state.loading = false;
        const splitClaims = action.payload || [];
        const originalId = state.selectedClaim?.id;
        const originalClaim = splitClaims.find(c => c.id === originalId);
        
        if (originalClaim) {
          state.selectedClaim = { ...state.selectedClaim, ...originalClaim };
        }
        
        if (state.claims && state.claims.length > 0) {
          const updatedClaims = [...state.claims];
          splitClaims.forEach(sc => {
            const idx = updatedClaims.findIndex(c => c.id === sc.id);
            if (idx !== -1) {
              updatedClaims[idx] = sc;
            } else {
              updatedClaims.unshift(sc); // New ones on top
            }
          });
          state.claims = updatedClaims;
        }
      })
      .addCase(splitClaim.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
      })

      // fetchPatientClaimsHistory
      .addCase(fetchPatientClaimsHistory.pending, (state) => {
        state.patientHistoryClaimsLoading = true;
      })
      .addCase(fetchPatientClaimsHistory.fulfilled, (state, action) => {
        state.patientHistoryClaimsLoading = false;
        const payload = action.payload || {};
        state.patientHistoryClaims = payload.data || [];
      })
      .addCase(fetchPatientClaimsHistory.rejected, (state, action) => {
        state.patientHistoryClaimsLoading = false;
        // checking specifically for history so we don't necessarily set the global error
        console.error("Failed to fetch patient history:", action.payload);
      })

      .addCase(createClaim.pending, (state) => {
        state.loading = true;

        state.error = null;
      })
      .addCase(createClaim.fulfilled, (state, action) => {
        state.loading = false;
        const newClaim = action.payload;
        if (newClaim) state.claims.unshift(newClaim);
        state.totalClaims += 1;
      })
      .addCase(createClaim.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
      })

      // updateClaimMedicationsBilling
      .addCase(updateClaimMedications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateClaimMedications.fulfilled, (state, action) => {
        state.loading = false;
        const claim = action.payload?.claim || action.payload;
        if (!claim) return;

        const index = state.claims.findIndex(c => c.id === claim?.id);
        if (index !== -1) state.claims[index] = claim;
        if (state.selectedClaim?.id === claim?.id) state.selectedClaim = claim;
      })
      .addCase(updateClaimMedications.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
      })

      // fetchClaimWithRelatedData
      .addCase(fetchClaimWithRelatedData.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchClaimWithRelatedData.fulfilled, (state, action) => {
        state.loading = false;
        state.selectedClaim = action.payload;
      })
      .addCase(fetchClaimWithRelatedData.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
      })

      // devUpdateClaimStatus
      .addCase(devUpdateClaimStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(devUpdateClaimStatus.fulfilled, (state, action) => {
        state.loading = false;
        const updatedClaim = action.payload;
        const index = state.claims.findIndex(c => c.id === updatedClaim.id);
        if (index !== -1) state.claims[index] = updatedClaim;
        if (state.selectedClaim?.id === updatedClaim.id) state.selectedClaim = updatedClaim;
      })
      .addCase(devUpdateClaimStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
      })

      // updateClaimFinancials
      .addCase(updateClaimFinancials.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateClaimFinancials.fulfilled, (state, action) => {
        state.loading = false;
        const updatedClaim = action.payload;
        const index = state.claims.findIndex(c => c.id === updatedClaim.id);
        if (index !== -1) state.claims[index] = updatedClaim;
        if (state.selectedClaim?.id === updatedClaim.id) state.selectedClaim = updatedClaim;
      })
      .addCase(updateClaimFinancials.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || action.error?.message;
      })

      // Create Appeal
      .addCase(createAppeal.pending, (state) => {
        state.appealLoading = true;
        state.appealError = null;
      })
      .addCase(createAppeal.fulfilled, (state, action) => {
        state.appealLoading = false;
        const appeal = normalizeAppealResponse(action.payload) || action.payload;
        if (appeal) {
          // add to top-level appeals list
          state.appeals = state.appeals || [];
          state.appeals.push(appeal);

          // ensure selectedClaim has appeals and update claim-level data immediately
          if (state.selectedClaim && state.selectedClaim.id) {
            state.selectedClaim.appeals = state.selectedClaim.appeals || [];
            state.selectedClaim.appeals.push(appeal);
            state.selectedClaim.status = 'appealing';
            state.selectedClaim.currentAppealId = appeal.id;
          }
        }
      })
      .addCase(createAppeal.rejected, (state, action) => {
        state.appealLoading = false;
        state.appealError = action.payload;
      })

      // Fetch Appeals
      .addCase(fetchAppeals.pending, (state) => {
        state.appealLoading = true;
        state.appealError = null;
      })
      .addCase(fetchAppeals.fulfilled, (state, action) => {
        state.appealLoading = false;
        state.appeals = action.payload.appeals;
        if (
          action.payload.mode !== 'list'
          && state.selectedClaim
          && state.selectedClaim.id === action.payload.claimId
        ) {
          state.selectedClaim.appeals = action.payload.appeals;
        }
      })
      .addCase(fetchAppeals.rejected, (state, action) => {
        state.appealLoading = false;
        state.appealError = action.payload || action.error?.message;
      })

      // Get Appeal By Id
      .addCase(getAppealById.fulfilled, (state, action) => {
        const index = state.appeals.findIndex(a => a.id === action.payload.id);
        if (index !== -1) {
          state.appeals[index] = action.payload;
        }
      })

      // Update Appeal
      .addCase(updateAppeal.fulfilled, (state, action) => {
        const appeal = normalizeAppealResponse(action.payload) || action.payload;
        if (!appeal) return;
        const index = state.appeals.findIndex(a => a.id === appeal.id);
        if (index !== -1) state.appeals[index] = { ...state.appeals[index], ...appeal };
        else state.appeals.push(appeal);

        if (state.selectedClaim) {
          state.selectedClaim.appeals = state.selectedClaim.appeals || [];
          const claimAppealIndex = state.selectedClaim.appeals.findIndex(a => a.id === appeal.id);
          if (claimAppealIndex !== -1) {
            state.selectedClaim.appeals[claimAppealIndex] = { ...state.selectedClaim.appeals[claimAppealIndex], ...appeal };
          } else {
            state.selectedClaim.appeals.push(appeal);
          }
        }
      })

      // Submit Appeal
      .addCase(submitAppeal.fulfilled, (state, action) => {
        const appeal = normalizeAppealResponse(action.payload) || action.payload;
        if (!appeal) return;
        const index = state.appeals.findIndex(a => a.id === appeal.id);
        if (index !== -1) state.appeals[index] = { ...state.appeals[index], ...appeal };
        else state.appeals.push(appeal);

        if (state.selectedClaim) {
          state.selectedClaim.status = 'submitted';
          state.selectedClaim.appeals = state.selectedClaim.appeals || [];
          const claimAppealIndex = state.selectedClaim.appeals.findIndex(a => a.id === appeal.id);
          if (claimAppealIndex !== -1) {
            state.selectedClaim.appeals[claimAppealIndex] = { ...state.selectedClaim.appeals[claimAppealIndex], ...appeal };
          } else {
            state.selectedClaim.appeals.push(appeal);
          }
          state.selectedClaim.currentAppealId = appeal.id;
        }
      })

      // Resolve Appeal
      .addCase(resolveAppeal.fulfilled, (state, action) => {
        state.appealLoading = false;
        const { appealId, resolvedAppeal } = action.payload || {};
        const resolved = normalizeAppealResponse(resolvedAppeal) || resolvedAppeal;

        // update top-level appeals
        const idx = state.appeals.findIndex(a => a.id === (appealId || resolved?.id));
        if (idx !== -1) {
          state.appeals[idx] = { ...state.appeals[idx], ...(resolved || {}) };
        } else if (resolved) {
          state.appeals.push(resolved);
        }

        // update selectedClaim.appeals
        if (state.selectedClaim?.appeals) {
          const claimAppealIndex = state.selectedClaim.appeals.findIndex(a => a.id === (appealId || resolved?.id));
          if (claimAppealIndex !== -1) {
            state.selectedClaim.appeals[claimAppealIndex] = {
              ...state.selectedClaim.appeals[claimAppealIndex],
              ...(resolved || {})
            };
          } else if (resolved) {
            state.selectedClaim.appeals.push(resolved);
          }
        }

        // If resolved response includes medicationBreakdown updates, merge them into selectedClaim
        if (resolved?.medicationBreakdown && state.selectedClaim?.medicationBreakdown) {
          const updatedMeds = resolved.medicationBreakdown;
          state.selectedClaim.medicationBreakdown = state.selectedClaim.medicationBreakdown.map(localMed => {
            const updated = updatedMeds.find(u => u.medicationId === localMed.medicationId);
            return updated ? { ...localMed, ...updated } : localMed;
          });
          if (resolved.status) {
            state.selectedClaim.status = resolved.status;
          }
        }

        // clear current appeal pointer
        if (state.selectedClaim) state.selectedClaim.currentAppealId = null;
      })
      .addCase(resolveAppeal.rejected, (state, action) => {
        state.appealLoading = false;
        state.appealError = action.payload;
      })

      // Bulk Appeals
      .addCase(createBulkAppeals.pending, (state) => {
        state.appealLoading = true;
        state.appealError = null;
      })
      .addCase(createBulkAppeals.fulfilled, (state, action) => {
        state.appealLoading = false;
        const { created = [] } = action.payload || {};
        if (created.length > 0) {
          state.appeals = state.appeals || [];
          state.appeals.push(...created);
        }
      })
      .addCase(createBulkAppeals.rejected, (state, action) => {
        state.appealLoading = false;
        state.appealError = action.payload;
      })

      .addCase(submitBulkAppeals.pending, (state) => {
        state.appealLoading = true;
        state.appealError = null;
      })
      .addCase(submitBulkAppeals.fulfilled, (state, action) => {
        state.appealLoading = false;
        const { submitted = [] } = action.payload || {};
        submitted.forEach(appeal => {
          const idx = state.appeals.findIndex(a => a.id === appeal.id);
          if (idx !== -1) state.appeals[idx] = { ...state.appeals[idx], ...appeal };
        });
      })
      .addCase(submitBulkAppeals.rejected, (state, action) => {
        state.appealLoading = false;
        state.appealError = action.payload;
      })

      .addCase(resolveBulkAppeals.pending, (state) => {
        state.appealLoading = true;
        state.appealError = null;
      })
      .addCase(resolveBulkAppeals.fulfilled, (state, action) => {
        state.appealLoading = false;
        const { resolved = [] } = action.payload || {};
        resolved.forEach(appeal => {
          const idx = state.appeals.findIndex(a => a.id === appeal.id);
          if (idx !== -1) state.appeals[idx] = { ...state.appeals[idx], ...appeal };
        });
      })
      .addCase(resolveBulkAppeals.rejected, (state, action) => {
        state.appealLoading = false;
        state.appealError = action.payload;
      })

      // fetchClaimForms
      .addCase(fetchClaimForms.pending, (state, action) => {
        const claimId = typeof action.meta.arg === 'string' ? action.meta.arg : action.meta.arg?.claimId;
        if (!state.claimForms[claimId]) {
          state.claimForms[claimId] = createEmptyClaimFormsState();
        }
        state.claimForms[claimId].loading = true;
        state.claimForms[claimId].error = null;
      })
      .addCase(fetchClaimForms.fulfilled, (state, action) => {
        const { claimId, cms1500, rxTransaction, paLibc9, njForm, byProvider } = action.payload;
        state.claimForms[claimId] = {
          cms1500,
          rxTransaction,
          paLibc9,
          njForm,
          loading: false,
          error: null,
          byProvider: byProvider || {},
        };
      })
      .addCase(fetchClaimForms.rejected, (state, action) => {
        const claimId = typeof action.meta.arg === 'string' ? action.meta.arg : action.meta.arg?.claimId;
        if (!state.claimForms[claimId]) {
          state.claimForms[claimId] = createEmptyClaimFormsState();
        }
        state.claimForms[claimId].loading = false;
        state.claimForms[claimId].error = action.payload;
      })
      // When document is uploaded, update the form reference
      .addCase(uploadDocument.fulfilled, (state, action) => {
        const doc = action.payload.document || action.payload;
        const claimId = doc.claimId;

        if (doc.type === 'CLAIM_FORMS' && claimId) {
          if (!state.claimForms[claimId]) {
            state.claimForms[claimId] = createEmptyClaimFormsState();
          }
          const providerKey = String(doc.providerId || doc.providerIds?.[0] || 'no-provider');
          if (!state.claimForms[claimId].byProvider) {
            state.claimForms[claimId].byProvider = {};
          }
          if (!state.claimForms[claimId].byProvider[providerKey]) {
            state.claimForms[claimId].byProvider[providerKey] = { cms1500: null, rxTransaction: null, paLibc9: null, njForm: null };
          }

          if (doc.subType === 'CMS1500') {
            state.claimForms[claimId].cms1500 = doc;
            state.claimForms[claimId].byProvider[providerKey].cms1500 = doc;
          } else if (doc.subType === 'RX_AUDIT') {
            state.claimForms[claimId].rxTransaction = doc;
            state.claimForms[claimId].byProvider[providerKey].rxTransaction = doc;
          } else if (doc.subType === 'PA-LIBC-9') {
            state.claimForms[claimId].paLibc9 = doc;
            state.claimForms[claimId].byProvider[providerKey].paLibc9 = doc;
          } else if (doc.subType === 'NJFORM') {
            state.claimForms[claimId].njForm = doc;
            state.claimForms[claimId].byProvider[providerKey].njForm = doc;
          }
        }
      })
      // When document is deleted, clear form reference
      .addCase(deleteDocument.fulfilled, (state, action) => {
        const documentId = action.payload;
        Object.keys(state.claimForms).forEach(claimId => {
          ['cms1500', 'rxTransaction', 'paLibc9', 'njForm'].forEach(formType => {
            if (state.claimForms[claimId][formType]?.id === documentId) {
              state.claimForms[claimId][formType] = null;
            }
          });
          Object.keys(state.claimForms[claimId].byProvider || {}).forEach((providerKey) => {
            ['cms1500', 'rxTransaction', 'paLibc9', 'njForm'].forEach((formType) => {
              if (state.claimForms[claimId].byProvider[providerKey]?.[formType]?.id === documentId) {
                state.claimForms[claimId].byProvider[providerKey][formType] = null;
              }
            });
          });
        });
      })
      // When document metadata is updated (sent, verified status)
      .addCase(updateDocument.fulfilled, (state, action) => {
        const updatedDoc = action.payload;
        Object.keys(state.claimForms).forEach(claimId => {
          ['cms1500', 'rxTransaction', 'paLibc9', 'njForm'].forEach(formType => {
            if (state.claimForms[claimId][formType]?.id === updatedDoc.id) {
              state.claimForms[claimId][formType] = updatedDoc;
            }
          });
          Object.keys(state.claimForms[claimId].byProvider || {}).forEach((providerKey) => {
            ['cms1500', 'rxTransaction', 'paLibc9', 'njForm'].forEach((formType) => {
              if (state.claimForms[claimId].byProvider[providerKey]?.[formType]?.id === updatedDoc.id) {
                state.claimForms[claimId].byProvider[providerKey][formType] = updatedDoc;
              }
            });
          });
        });
      })

      // Fetch Claim Status Counts
      .addCase(fetchClaimStatusCounts.pending, (state) => {
        state.statusCountsLoading = true;
      })
      .addCase(fetchClaimStatusCounts.fulfilled, (state, action) => {
        state.statusCountsLoading = false;
        state.statusCounts = action.payload || {};
      })
      .addCase(fetchClaimStatusCounts.rejected, (state) => {
        state.statusCountsLoading = false;
      });
  },
});

export const {
  setFilters,
  resetFilters,
  setReconFilters,
  resetReconFilters,
  clearFilters,
  setPage,
  setLimit,
  clearError,
  selectClaim,
  clearSelectedClaim,
  updateCaseTypeOptimistically,
  clearAppealError,
  setAppealFilters,
  setClaims,
  setAppealClaims,
  setRecentlyUpdatedClaimIds,
  clearRecentlyUpdatedClaimIds
} = claimsSlice.actions;

export default claimsSlice.reducer;
